import React, { useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, useLocation } from "react-router-dom";
import { ThemeProvider, CssBaseline, Box } from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import { supabase } from "./supabaseClient";
import Auth from "./components/Auth";
import CharacterManager from "./pages/CharacterManager";
import CharacterList from "./pages/CharacterList";
import CharacterVoting from "./pages/CharacterVoting";
import Leaderboard from "./pages/Leaderboard";
import NavBar from "./components/NavBar";
import VersusLive from "./pages/VersusLive";
import Profile from "./pages/Profile";
import VersusLobby from "./pages/VersusLobby";
import MainPage from "./pages/MainPage";
import Footer from "./components/Footer";
import theme from "./theme";
import "./App.css";
import LazyImage from "./components/LazyImage"; // Add this import

// Page transition variants
const pageVariants = {
  initial: {
    opacity: 0,
    y: 20, // Slight upward movement for a smooth entrance
  },
  in: {
    opacity: 1,
    y: 0,
  },
  out: {
    opacity: 0,
    y: -20, // Slight downward movement for exit
  },
};

const pageTransition = {
  type: "tween",
  ease: "anticipate",
  duration: 0.4,
};

// Video background component
function VideoBackground() {
  // Use a <video> tag with preload and poster for better caching
  return (
    <>
      {/* Video Background */}
      <Box
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          zIndex: -2,
          overflow: "hidden",
        }}
      >
        <video
          autoPlay
          loop
          muted
          playsInline
          preload="auto" // Instructs browser to cache video
          poster="https://wwwikvfwquvafkoopemw.supabase.co/storage/v1/object/public/manga_versus/videos/background/backgroundmanga_loop1_poster.webp"
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            objectPosition: "center",
            filter: "hue-rotate(60deg) saturate(1.2)",
          }}
        >
          <source
            src="https://wwwikvfwquvafkoopemw.supabase.co/storage/v1/object/public/manga_versus/videos/background/backgroundmanga_loop1.mp4"
            type="video/mp4"
          />
        </video>
      </Box>

      {/* Black Glass Blur Overlay */}
      <Box
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          zIndex: -1,
          background: "rgba(0, 0, 0, 0.7)",
          backdropFilter: "blur(4px)",
          WebkitBackdropFilter: "blur(2px)",
        }}
      />
    </>
  );
}

// Wrapper component to handle page transitions
function AnimatedRoutes() {
  const location = useLocation();

  return (
    <AnimatePresence mode="wait" initial={false}>
      <motion.div
        key={location.pathname}
        initial="initial"
        animate="in"
        exit="out"
        variants={pageVariants}
        transition={pageTransition}
        style={{
          position: "absolute",
          width: "100%",
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box sx={{ flex: 1, pb: 2 }}> {/* Content area that takes remaining space */}
          <Routes location={location}>
            <Route path="/" element={<MainPage />} />
            <Route path="/versus-live" element={<VersusLive />} />
            <Route path="/versus-lobby" element={<VersusLobby />} />
            <Route path="/character-list" element={<CharacterList />} />
            <Route path="/character-voting" element={<CharacterVoting />} />
            <Route path="/character-manager" element={<CharacterManager />} />
            <Route path="/leaderboard" element={<Leaderboard />} />
            <Route path="/profile" element={<Profile />} />
            <Route path="/profile/:username" element={<Profile />} />
          </Routes>
        </Box>
        <Footer />
      </motion.div>
    </AnimatePresence>
  );
}

function App() {
  const [session, setSession] = useState(null);

  useEffect(() => {
    const getSession = async () => {
      const { data } = await supabase.auth.getSession();
      setSession(data.session);
    };

    getSession();

    const { data: listener } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        setSession(session);
      }
    );

    return () => {
      listener.subscription.unsubscribe();
    };
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <div style={{ position: "relative", minHeight: "100vh" }}>
          <VideoBackground />
          <NavBar />
          <AnimatedRoutes />
        </div>
      </Router>
    </ThemeProvider>
  );
}

export default App;
